import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CatalogPlan } from '@shared/models/plan-catalog.model';
import { OnboardingPlanSelection } from '../../domain/models/onboarding-plan.model';
import { OnboardingStatus, OnboardingTrialCodeRedemption } from '../../domain/models/onboarding-status.model';
import { OrganizationOverview } from '../../domain/models/organization-overview.model';
import { PersonalData } from '../../domain/models/personal-data.model';
import { StaffMember } from '../../domain/models/staff-member.model';
import { OnboardingTeamDraft, OnboardingTeamDraftSelection } from '../../domain/models/onboarding-team-draft.model';
import {
  CheckoutStatusResult,
  PaymentConfig,
  SubscriptionCheckout,
} from '../../domain/models/subscription-checkout.model';
import {
  IOnboardingRepository,
  OnboardingStepNumber,
} from '../../domain/repositories/onboarding.repository';
import { OnboardingRemoteDatasource } from '../datasources/remote/onboarding.remote-datasource';
import { OnboardingMapper } from '../mappers/onboarding.mapper';

@Injectable()
export class OnboardingRepositoryImpl implements IOnboardingRepository {
  private readonly datasource = inject(OnboardingRemoteDatasource);
  private readonly mapper = inject(OnboardingMapper);

  public getStatus(): Observable<OnboardingStatus> {
    return this.datasource.getStatus().pipe(map((dto) => this.mapper.toStatus(dto)));
  }

  public getPersonalData(): Observable<PersonalData> {
    return this.datasource.getPersonalData().pipe(map((dto) => this.mapper.toPersonalData(dto)));
  }

  public getOrganization(): Observable<OrganizationOverview> {
    return this.datasource.getOrganization().pipe(map((dto) => this.mapper.toOrganizationOverview(dto)));
  }

  public getStaff(): Observable<readonly StaffMember[]> {
    return this.datasource.getStaff().pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toStaffMember(item))),
    );
  }

  public getTeamDraft(): Observable<OnboardingTeamDraft> {
    return this.datasource.getTeamDraft().pipe(map((dto) => this.mapper.toTeamDraft(dto)));
  }

  public saveTeamDraft(
    revision: number,
    members: readonly OnboardingTeamDraftSelection[],
  ): Observable<OnboardingTeamDraft> {
    return this.datasource
      .saveTeamDraft(this.mapper.toSaveTeamDraftRequest(revision, members))
      .pipe(map((dto) => this.mapper.toTeamDraft(dto)));
  }

  public getPlans(): Observable<readonly CatalogPlan[]> {
    return this.datasource.getPlans().pipe(map((dtos) => this.mapper.toPlans(dtos)));
  }

  public selectPlan(selection: OnboardingPlanSelection): Observable<OnboardingStatus> {
    return this.datasource
      .selectPlan(this.mapper.toSelectPlanRequest(selection))
      .pipe(map((dto) => this.mapper.toStatus(dto)));
  }

  public redeemTrialCode(code: string): Observable<OnboardingTrialCodeRedemption> {
    return this.datasource
      .redeemTrialCode(this.mapper.toRedeemTrialCodeRequest(code))
      .pipe(map((dto) => this.mapper.toTrialCodeRedemption(dto)));
  }

  public startCheckout(selection: OnboardingPlanSelection): Observable<SubscriptionCheckout> {
    return this.datasource
      .startCheckout(this.mapper.toStartCheckoutRequest(selection))
      .pipe(map((dto) => this.mapper.toSubscriptionCheckout(dto)));
  }

  public getCheckoutStatus(sessionId: string): Observable<CheckoutStatusResult> {
    return this.datasource
      .getCheckoutStatus(sessionId)
      .pipe(map((dto) => this.mapper.toCheckoutStatusResult(dto)));
  }

  public getPaymentConfig(): Observable<PaymentConfig> {
    return this.datasource.getPaymentConfig().pipe(map((dto) => this.mapper.toPaymentConfig(dto)));
  }

  public completeStep(stepNumber: OnboardingStepNumber): Observable<OnboardingStatus> {
    return this.datasource.completeStep(stepNumber).pipe(map((dto) => this.mapper.toStatus(dto)));
  }
}
